import { ExportOutlined } from '@ant-design/icons';
import { Flex, message, Spin } from 'antd';
import { Key, SortOrder } from 'antd/es/table/interface';
import { AxiosError } from 'axios';
import { Dayjs } from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import packageJson from '../../package.json';
import {
  ALLOWED_USER_ROLE,
  STAKEHOLDER_TYPES,
  STATUS_MAP,
  USERS_ROLE,
} from './constants';
import DateTime from './datetime';

export const getStatusValues = (status?: string) => {
  const statusValue = status?.toUpperCase();
  return (
    STATUS_MAP?.[statusValue as keyof typeof STATUS_MAP] || {
      ...STATUS_MAP.PENDING,
      text: status,
    }
  );
};

export const getGoogleMapURL = (x = '', y = '') => {
  return `http://maps.google.com/maps?q=${y},${x}`;
};

export const renderInfoValue = (
  value?: React.ReactNode,
  fallback: React.ReactNode = '-',
) => {
  if (value === 0) return 0;
  if (!value || value === 'undefined' || value === 'null') return fallback;
  return value;
};

export const renderDate = (
  value?: string | number | Date | Dayjs | null,
  fallback = '-',
  format?: string,
) => {
  return value
    ? DateTime.formatIfValid(value, format ?? 'MMM DD, YYYY')
    : fallback;
};

export const renderLocation = (
  location?: { x?: string | number; y?: string | number },
  fallback = '-',
) => {
  const x = location?.x;
  const y = location?.y;
  return x &&
    y &&
    x !== 'undefined' &&
    y !== 'undefined' &&
    x !== 'null' &&
    y !== 'null' ? (
    <a
      className="link-text"
      href={getGoogleMapURL(x?.toString(), y?.toString())}
      target="_blank"
      rel="noreferrer"
    >
      {`${location?.x}, ${location?.y}`} <ExportOutlined />
    </a>
  ) : (
    <p>{fallback}</p>
  );
};

export const validateImageURL = (url: string) => {
  const regex = /^(http|https):\/\/.*\.(jpeg|jpg|gif|png|bmp|webp|svg)$/i;
  return regex.test(url);
};

export const capitalizeWords = (str: string) => {
  return str
    ? str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
    : '';
};

export const isDevOrStaging = () => {
  const url = window.location.origin;
  return url.includes('console-staging') || url.includes('localhost');
};

export const getCountryPhoneCode = (country?: string) => {
  const countryName = country?.toLowerCase();
  switch (countryName) {
    case 'indonesia':
      return '62';
    case 'test':
      return '62';
    case 'vietnam':
      return '84';
    default:
      return '';
  }
};

export const removePhoneCountryCode = (
  phoneNumber?: string,
  country?: string,
) => {
  const phoneCode = getCountryPhoneCode(country);
  if (!!phoneCode && phoneNumber?.substring(0, 2) === phoneCode)
    return phoneNumber.substring(2);
  return phoneNumber;
};

export const downloadFile = (
  dataOrUrl: Blob | string,
  fileName: string,
  type = 'text/csv',
) => {
  let url: string;
  const isUrl = typeof dataOrUrl === 'string';
  if (typeof dataOrUrl === 'string') {
    url = dataOrUrl;
  } else {
    const blob = new Blob([dataOrUrl], { type: type });
    url = URL.createObjectURL(blob);
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  if (isUrl) a.download = url.split('/')?.pop() || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const decodeToken = (token: string) => {
  if (!token) return;
  return jwtDecode<{ scope: string }>(token);
};

export const checkUserRole = (token: string, scopeToCheck: string) => {
  if (!token || !scopeToCheck) return;

  const decoded = decodeToken(token);

  if (!decoded || !decoded?.['scope']) return;

  return decoded?.['scope'] === scopeToCheck;
};

export const getAppVersion = () => packageJson?.version || '';

export const getRoleFromToken = (token?: string) => {
  if (!token) return '';
  const decoded = jwtDecode<{ scope: string }>(token);
  return decoded['scope'];
};

export const getDisplayRole = (role?: string) => {
  switch (role) {
    case 'PM':
      return 'Product Manager';
    case 'GM':
      return 'General Manager';
    case 'Admin':
      return 'Admin';
    case 'Sr Agronomist':
      return 'Senior Agronomist';
    case 'Area Manager':
      return 'Area Manager';
    default:
      return USERS_ROLE.find((item) => item.value === role)?.label;
  }
};

/**
 * Checks if a user is authorized to access a resource.
 *
 * By default, a user is authorized if their role is 'Admin', 'Sr Agronomist', 'GM', 'PM', or 'Area Manager', 'JR Agronomist', or 'JR Agronomist (External)'.
 * If a list of roles is provided, the user is authorized if their role is in the list.
 *
 * @param {string} token User's token
 * @param {string[]} [roles] Optional list of roles. If provided, the user is authorized if their role is in the list.
 * @returns {boolean} True if the user is authorized, false otherwise
 */
export const isAuthorizedToAccessResource = (
  token: string,
  roles: string[] | null,
) => {
  const authorizedRoles = roles || ALLOWED_USER_ROLE;
  const isUserAuthorized = authorizedRoles.some((role) =>
    checkUserRole(token, role),
  );
  return isUserAuthorized;
};

export const formatNumberWithComma = (number?: number | string) =>
  number?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const getCountryCurrency = (country: string) => {
  const CURRENCY = {
    Indonesia: 'IDR',
    Vietnam: 'VND',
  };
  return CURRENCY[country as keyof typeof CURRENCY] ?? 'USD';
};

// Recursively removes null, undefined, and empty string values from an object or array
export const cleanObject = <T,>(obj: T): T => {
  // If the value is not an object (e.g., primitive, null, undefined), return it directly
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // If the value is an array, recursively clean each item
  if (Array.isArray(obj)) {
    return obj
      .map((item) => cleanObject(item))
      .filter(
        (item) => item !== null && item !== undefined && item !== '',
      ) as unknown as T;
  }

  // If the value is an object, recursively clean its properties
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const cleanedValue = cleanObject(value);
        acc[key as keyof T] = cleanedValue;
      }
      return acc;
    },
    {} as { [K in keyof T]: T[K] },
  ) as T;
};

export const handleAxiosError = (
  error: unknown,
  defaultErrorMessage = 'Request failed. Please try again',
) => {
  if (error instanceof AxiosError) {
    if (error.response?.data?.detail) {
      message.error(
        error?.response?.data?.detail
          ?.replace(/^.*?"/, '')
          ?.replace(/["\\]/g, ''),
      );
    } else if (error.response?.data?.title) {
      message.error(error.response?.data?.title);
    } else {
      message.error(error?.message);
    }
  } else {
    message.error(defaultErrorMessage);
  }
};

type AppOptions = {
  value?: string | number;
  label?: React.ReactNode;
  disabled?: boolean;
  status?: string; // <-- ADD THIS LINE
  [x: string]: unknown;
};

export const populateOptions = (
  staticOption?: AppOptions,
  optionList?: AppOptions[],
  loading = false,
): AppOptions[] => {
  return [
    ...(staticOption ? [staticOption] : []),
    ...(optionList?.filter((val) => val?.value !== staticOption?.value) ??
      (loading
        ? [
            {
              value: '',
              label: (
                <Flex justify="center" align="center">
                  <Spin />
                </Flex>
              ),
              disabled: true,
            },
          ]
        : [])),
  ];
};

export const copyToClipboard = (text: string) => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  }

  throw new Error('Clipboard not available');
};

export const isMobileScreenSize = window.innerWidth <= 576;

export const formatCurrencyShortened = (value?: number) =>
  value
    ? Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
    : 0;

export const tableSortHandler = (
  sort: { columnKey?: Key; order?: SortOrder } | null,
  key: string,
) => {
  return sort?.columnKey === key ? sort?.order : null;
};

export const calcPercentage = (current: number, total: number) => {
  return ((current / total) * 100).toFixed(2);
};

/**
 * Returns the stakeholder type based on the country and type.
 * If the country or type is not found, it returns the original type.
 *
 * @param {string} country - The country for which to get the stakeholder type.
 * @param {"PB" | "FGL" | "MachineOwner"} type - The type of stakeholder .
 * @returns {string} - The stakeholder type for the given country and type.
 */
export const getStakeholderTypeForCountry = (country: string, type: 'PB') => {
  return (
    STAKEHOLDER_TYPES?.[country as keyof typeof STAKEHOLDER_TYPES]?.[type] ||
    STAKEHOLDER_TYPES?.Indonesia?.['PB']
  );
};

export const camelCaseToWords = (s: string) => {
  const result = s?.replace(/([A-Z])/g, ' $1');
  return result?.charAt(0).toUpperCase() + result?.slice(1);
};

export const matchTwoArrays = (arr1: unknown[], arr2: unknown[]) => {
  const searchSet = new Set(arr2 || []);
  const matchFound = arr1?.some((element) => searchSet.has(element));
  return matchFound;
};
